// Lista de salas abertas (canvas T01, 3.1 e 3.1b), no centro da aba Jogar: abas Abertas e Valendo Tentos, os
// filtros Só com amigos e Com vaga para trio e a lista, que se atualiza a cada 10 s sem pular enquanto a pessoa rola.
// Toda sala é pública. As salas com amigos sentados ficam no topo, na seção COM AMIGOS (a mesa calcula para quem
// pediu); os filtros valem juntos. O cartão mostra em chips as regras que fogem do padrão ("Sem zoeira", "30 s por
// jogada") e tem "Denunciar nome": uma denúncia por conta Google; com 3, a sala sai da lista até a revisão. A partida
// em andamento com vaga aberta mostra "1 vaga", o placar e Sentar (a pessoa entra na próxima mão).
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import type { RegrasNoCartao, SalaNaLista } from '@truco6/mesa/protocolo';
import { DEFAULT_HOUSE_RULES } from '@truco6/regras';
import { DENUNCIAS_PARA_OCULTAR, pode } from '@truco6/planos';
import { mesaHttp } from '../comum/config.ts';
import { type AmigoNaLista, lerAmigos } from '../comum/amigos.ts';
import { IconeAmigos } from '../comum/BotaoAmizade.tsx';
import { Rosto } from '../comum/Rosto.tsx';
import type { PerfilConta, Sessao } from '../comum/conta.ts';
import { denunciarSala, jaDenunciou } from '../comum/denuncia.ts';
import { ir, type Rota } from '../comum/rotas.ts';
import { useSemPartidas } from '../comum/SeloGratis.tsx';
import { imagemDoAvatar } from '@truco6/ui';
import { FolhaDePessoas } from '../comum/FolhaDePessoas.tsx';
import { Tento, linhaDoSaldo, usePainelTentos } from './tentos-comum.tsx';

export type AbaDeSalas = 'abertas' | 'tentos';
const ATUALIZAR_MS = 10_000;
const ROLANDO_MS = 2_000; // a lista nova espera a pessoa parar de rolar
const CHAVE_SO_AMIGOS = 'truco6:salas-so-amigos';

const amigosDa = (s: SalaNaLista) => s.amigosNaSala?.length ?? 0;

// Valendo Tentos: com vaga primeiro, depois pelo ingresso, dentro de cada seção (as salas com amigos seguem no topo,
// a de mais amigos primeiro). Abertas: como vem do servidor.
export function ordenar(salas: SalaNaLista[], aba: AbaDeSalas): SalaNaLista[] {
  if (aba === 'abertas') return salas;
  return salas.filter((s) => s.ingresso !== null)
    .sort((a, b) => Number(amigosDa(b) > 0) - Number(amigosDa(a) > 0) || amigosDa(b) - amigosDa(a)
      || Number(b.vagas > 0) - Number(a.vagas > 0) || a.ingresso! - b.ingresso!);
}

// "Bia está aqui", "Bia e Rafa estão aqui", "Bia, Rafa e mais 2 estão aqui".
export function linhaDosAmigos(nomes: string[]): string {
  if (nomes.length === 1) return `${nomes[0]} está aqui`;
  if (nomes.length === 2) return `${nomes[0]} e ${nomes[1]} estão aqui`;
  return `${nomes[0]}, ${nomes[1]} e mais ${nomes.length - 2} estão aqui`;
}

// As regras da casa que fogem do padrão, em chips curtos no cartão. Valendo Tentos, sem bot é da sala (não aparece).
export function chipsDasRegras(r: RegrasNoCartao, valendo: boolean): string[] {
  const out: string[] = [];
  if (r.sinais === 'todos') out.push('Sinais: todos veem');
  else if (r.sinais === 'desligados') out.push('Sem sinais');
  if (!r.zoeira) out.push('Sem zoeira');
  if (r.tempoPorJogadaMs !== DEFAULT_HOUSE_RULES.tempoPorJogadaMs) out.push(`${Math.round(r.tempoPorJogadaMs / 1000)} s por jogada`);
  if (!r.vagasComBot && !valendo) out.push('Sem bot');
  return out;
}

function lerSoAmigos(): boolean {
  try { return localStorage.getItem(CHAVE_SO_AMIGOS) === '1'; } catch { return false; }
}

// A lista de salas com as abas e os filtros, em grade: no celular em pé, uma coluna; a partir de 720 px (celular
// deitado e computador), `lado` (os botões da aba Jogar), as abas e os filtros à esquerda e a lista à direita.
// vazio: o convite para criar sala quando a aba Abertas não tem nenhuma; rodape: os avisos que vêm depois da lista;
// inicial: a lista antes da primeira resposta da mesa (testes e vitrine).
export function SalasAbertas({ sessao, perfil, aba, aoMudarAba, lado, vazio, rodape, inicial = null }: {
  sessao: Sessao; perfil: PerfilConta; aba: AbaDeSalas; aoMudarAba: (a: AbaDeSalas) => void; lado?: ReactNode; vazio?: ReactNode; rodape?: ReactNode;
  inicial?: SalaNaLista[] | null;
}) {
  const [salas, setSalas] = useState<SalaNaLista[] | null>(inicial);
  const [erro, setErro] = useState<string | null>(null);
  const { painel } = usePainelTentos(sessao);
  // Convidado não tem amigos: o filtro Só com amigos não aparece para ele.
  const [soAmigos, setSoAmigos] = useState(() => !sessao.anonimo && lerSoAmigos());
  const [vagaTrio, setVagaTrio] = useState(false);
  const rolouEm = useRef(0);

  // Quem está rolando a lista não vê ela pular: a lista nova espera a rolagem parar.
  useEffect(() => {
    const marcar = () => { rolouEm.current = Date.now(); };
    document.addEventListener('scroll', marcar, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', marcar, { capture: true });
  }, []);

  useEffect(() => {
    let vivo = true, adiada: ReturnType<typeof setTimeout> | null = null;
    const q = new URLSearchParams();
    if (soAmigos) q.set('soComAmigos', '1');
    if (vagaTrio) q.set('vagaTrio', '1');
    function aplicar(s: SalaNaLista[]) {
      const espera = ROLANDO_MS - (Date.now() - rolouEm.current);
      if (espera > 0) { if (adiada) clearTimeout(adiada); adiada = setTimeout(() => aplicar(s), espera); return; }
      setSalas(s);
    }
    async function carregar() {
      try {
        // Com a conta, a mesa marca os amigos de quem pede em cada sala.
        const r = await fetch(mesaHttp + '/salas' + (q.size ? '?' + q : ''), { cache: 'no-store', headers: { authorization: 'Bearer ' + (await sessao.token()) } });
        if (!r.ok) throw new Error();
        const { salas: s } = (await r.json()) as { salas: SalaNaLista[] };
        if (vivo) { aplicar(s); setErro(null); }
      } catch { if (vivo) setErro('A lista de salas não carregou. Tentando de novo...'); }
    }
    void carregar();
    const t = setInterval(() => { void carregar(); }, ATUALIZAR_MS);
    return () => { vivo = false; clearInterval(t); if (adiada) clearTimeout(adiada); };
  }, [soAmigos, vagaTrio, sessao.id]);

  function mudarSoAmigos() {
    const v = !soAmigos;
    setSoAmigos(v);
    setSalas(null);
    try { localStorage.setItem(CHAVE_SO_AMIGOS, v ? '1' : '0'); } catch { /* sem armazenamento: só não lembra */ }
  }

  const lista = salas ? ordenar(salas, aba) : [];
  const comAmigos = lista.filter((s) => amigosDa(s) > 0), outras = lista.filter((s) => amigosDa(s) === 0);
  const disponivel = painel ? Math.max(0, painel.disponivel) : null;
  const cartao = (s: SalaNaLista) => <CartaoSala key={s.id} s={s} sessao={sessao} disponivel={sessao.anonimo ? null : disponivel} />;

  return (
    <div className="grade-lista">
      <div className="lista-lado">
      {lado}
      {aba === 'tentos' && !sessao.anonimo && painel && (
        <section className="saldo-lista" aria-label={`Seu saldo: ${painel.saldo} Tentos`}>
          <Tento tamanho={30} brilho />
          <p><span className="saldo-num"><b>{painel.saldo}</b> Tentos</span><span className="nota">{linhaDoSaldo(painel)}</span></p>
          <button className="chip-saldo" onClick={() => ir({ tela: 'loja' })}><Tento tamanho={16} vinco={false} />Comprar</button>
        </section>
      )}
      <div className="abas-salas" role="tablist" aria-label="Tipos de sala">
        <button role="tab" aria-selected={aba === 'abertas'} className={aba === 'abertas' ? 'on' : undefined} onClick={() => aoMudarAba('abertas')}>Abertas</button>
        <button role="tab" aria-selected={aba === 'tentos'} className={aba === 'tentos' ? 'on' : undefined} onClick={() => aoMudarAba('tentos')}>
          <Tento tamanho={16} vinco={false} />Valendo Tentos
        </button>
      </div>
      <div className="filtros-salas" role="group" aria-label="Filtros">
        {!sessao.anonimo && (
          <button className={'filtro' + (soAmigos ? ' on' : '')} aria-pressed={soAmigos} onClick={mudarSoAmigos}>
            <IconeAmigos tamanho={16} />Só com amigos
          </button>
        )}
        <button className={'filtro' + (vagaTrio ? ' on' : '')} aria-pressed={vagaTrio} onClick={() => { setVagaTrio(!vagaTrio); setSalas(null); }}>
          Com vaga para trio
        </button>
      </div>
      </div>
      <div className="lista-principal">
      <div className="linha-lista">
        <span className="nota">{aba === 'tentos' ? 'Com vaga primeiro, por ingresso' : 'Com vaga primeiro'}</span>
        <span className="nota">{salas ? `${lista.length} ${lista.length === 1 ? 'sala' : 'salas'}` : ''}</span>
      </div>
      {erro && <p className="erro" role="alert">{erro}</p>}
      {!salas && !erro && <p className="carregando" role="status">Procurando salas...</p>}
      {salas && lista.length === 0 && (soAmigos ? <SemAmigosEmSala sessao={sessao} /> : aba === 'abertas' && vazio ? vazio : (
        <p className="cartao vazio-lista">
          {aba === 'tentos' ? 'Nenhuma sala valendo Tentos aberta agora.' : 'Nenhuma sala aberta agora.'} Crie a sua e chame a turma.
        </p>
      ))}
      {comAmigos.length > 0 && (
        <section className="secao-salas" aria-label={`Com amigos: ${comAmigos.length} ${comAmigos.length === 1 ? 'sala' : 'salas'}`}>
          <h2 className="rotulo">COM AMIGOS <span className="dourado">· {comAmigos.length}</span></h2>
          <div className="lista-salas">{comAmigos.map(cartao)}</div>
        </section>
      )}
      {outras.length > 0 && (
        <Fragment>
          {comAmigos.length > 0
            ? (
              <section className="secao-salas" aria-label="Outras salas">
                <h2 className="rotulo">OUTRAS SALAS</h2>
                <div className="lista-salas">{outras.map(cartao)}</div>
              </section>
            )
            : <div className="lista-salas">{outras.map(cartao)}</div>}
        </Fragment>
      )}
      {aba === 'tentos' && !sessao.anonimo && (
        <button className="btn2 largo criar-tentos" onClick={() => ir({ tela: 'nova-sala', ingresso: 6 })}>
          <Tento tamanho={18} vinco={false} />Criar sala valendo Tentos
          {!pode(perfil.plano, 'criarSalaComTentos') && <span className="selo-mini">Prata</span>}
        </button>
      )}
      {aba === 'tentos' && <p className="nota centro">Tentos não valem dinheiro. Não dá para sacar, trocar nem transferir.</p>}
      {rodape}
      </div>
    </div>
  );
}

// Filtro ligado e nenhuma sala: os amigos online fora de sala e o atalho para chamar a turma.
function SemAmigosEmSala({ sessao }: { sessao: Sessao }) {
  const [online, setOnline] = useState<AmigoNaLista[] | null>(null);
  useEffect(() => {
    let vivo = true;
    lerAmigos(sessao).then((r) => { if (vivo) setOnline(r.amigos.filter((a) => a.status.tipo === 'online')); }, () => { if (vivo) setOnline([]); });
    return () => { vivo = false; };
  }, [sessao.id]);
  return (
    <div className="cartao sem-amigos-em-sala">
      <p><b>Nenhum amigo em sala aberta agora</b></p>
      {online && online.length > 0 && (
        <>
          <p className="nota">Online fora de sala:</p>
          <ul className="lista-pessoas">
            {online.map((a) => (
              <li key={a.usuario}>
                <Rosto perfil={{ apelido: a.apelido, avatar: a.avatar }} usuario={a.usuario} cor="var(--gold-600)" tamanho={40} />
                <span className="nome"><b>{a.apelido}</b><small className="dourado">Online</small></span>
              </li>
            ))}
          </ul>
        </>
      )}
      <button className="btn2 largo chamar-amigos" onClick={() => ir({ tela: 'amigos' })}><IconeAmigos tamanho={20} />Chamar amigos para uma sala</button>
    </div>
  );
}

function CartaoSala({ s, sessao, disponivel }: { s: SalaNaLista; sessao: Sessao; disponivel: number | null }) {
  const [quem, setQuem] = useState(false);
  const valendo = s.ingresso !== null, cheia = s.vagas === 0;
  const andamento = s.fase === 'jogo', comVaga = andamento && s.vagas > 0;
  const faltam = valendo && disponivel !== null ? Math.max(0, s.ingresso! - disponivel) : 0;
  const amigos = s.amigosNaSala ?? [], nomes = amigos.map((a) => a.apelido);
  // Sala de antes dos chips (sem as regras no cartão): só o aviso de que tem regra da casa.
  const regras = s.regras ? chipsDasRegras(s.regras, valendo) : s.regrasDaCasa ? ['Regras da casa'] : [];
  const contagem = comVaga ? `${s.vagas} ${s.vagas === 1 ? 'vaga' : 'vagas'}` : `${s.sentados} de 6`;
  const placar = s.placar ? ` · ${String(s.placar[0]).padStart(2, '0')} x ${String(s.placar[1]).padStart(2, '0')}` : '';
  const rotulo = `${s.nome}${andamento ? ', partida em andamento' : ''}${valendo ? `, ${s.ingresso} Tentos por jogador, vencedor leva ${s.premio}` : ', grátis'}${regras.length ? `, ${regras.join(', ')}` : ''}`
    + `${comVaga ? `, ${s.vagas} ${s.vagas === 1 ? 'vaga aberta' : 'vagas abertas'}` : `, ${s.sentados} de 6 lugares`}`
    + `${amigos.length ? `, ${linhaDosAmigos(nomes)}` : ''}${faltam ? `, faltam ${faltam} Tentos` : ''}`;
  // Os rostos: os amigos primeiro, com borda dourada; depois os outros sentados, os bots nas vagas abertas (borda
  // dourada tracejada), os outros bots e os lugares vazios.
  const idsAmigos = new Set(amigos.map((a) => a.id));
  const pessoas = (s.jogadores ?? []).filter((j): j is { id: string; apelido: string; avatar: string | null } => 'id' in j);
  const rostos = [...pessoas.filter((j) => idsAmigos.has(j.id)), ...pessoas.filter((j) => !idsAmigos.has(j.id))];
  const bots = (s.jogadores ?? []).filter((j) => 'bot' in j), nasVagas = bots.filter((j) => 'vaga' in j && j.vaga).length;
  let botao: React.ReactNode;
  // Sem partidas grátis hoje, sentar leva à tela Limite. Assistir continua liberado.
  const sem = useSemPartidas(), sentar = (r: Rota) => ir(sem ? { tela: 'limite' } : r);
  // Partida em andamento: Assistir, ao lado do Sentar quando há vaga aberta.
  const assistir = andamento ? <button className="btn2 assistir" aria-label={`Assistir ${s.nome}`} onClick={() => ir({ tela: 'assistir', codigo: s.codigo, amigo: null })}><IconeOlhos />Assistir</button> : null;
  if (comVaga && !valendo) botao = <button className="btn2 entrar" onClick={() => sentar({ tela: 'sala', codigo: s.codigo, vaga: 'lista' })}>Sentar</button>;
  else if (andamento) botao = null;
  else if (cheia) botao = <button className="btn2" disabled>Cheia</button>;
  else if (faltam > 0) botao = <button className="btn2 faltam" onClick={() => ir({ tela: 'loja' })}>Faltam {faltam} Tentos</button>;
  else botao = <button className="btn2 entrar" onClick={() => sentar({ tela: 'sala', codigo: s.codigo })}>{valendo ? `Entrar · ${s.ingresso}` : 'Entrar'}</button>;
  return (
    <article className={'cartao-sala' + (valendo ? ' valendo' : '') + (amigos.length ? ' com-amigos' : '')} aria-label={rotulo}>
      <div className="cab-sala">
        <h2>{s.nome}</h2>
        {amigos.length > 0 && <span className="selo-amigos"><IconeAmigos tamanho={14} />{amigos.length} {amigos.length === 1 ? 'amigo' : 'amigos'}</span>}
        <span className={comVaga ? 'vagas-sala' : 'nota'}>{contagem}</span>
      </div>
      {andamento
        ? <p className="linha-andamento"><span className="ao-vivo" aria-hidden="true" /><span>Em andamento{placar}{amigos.length > 0 ? ' · ' : ''}</span>{amigos.length > 0 && <b>{linhaDosAmigos(nomes)}</b>}</p>
        : amigos.length > 0 && <p className="linha-amigos">{linhaDosAmigos(nomes)}</p>}
      <div className="meio-sala">
        {valendo
          ? <span className="chip-tentos"><Tento tamanho={16} vinco={false} />{s.ingresso} por jogador · leva {s.premio}</span>
          : <span className="chip-gratis">Grátis</span>}
        <Denunciar s={s} sessao={sessao} />
      </div>
      {regras.length > 0 && (
        <ul className="chips-regras" aria-label="Regras da casa">{regras.map((r) => <li key={r}>{r}</li>)}</ul>
      )}
      <div className="pe-sala">
        <button className="rostos" disabled={!s.jogadores?.length} onClick={() => setQuem(true)}
          aria-label={`Ver quem está na sala. Sentados: ${rostos.length || s.sentados} ${(rostos.length || s.sentados) === 1 ? 'jogador' : 'jogadores'}.`
            + (comVaga ? ` ${s.vagas} ${s.vagas === 1 ? 'vaga aberta, com bot' : 'vagas abertas, com bot'}` : ` ${s.vagas} ${s.vagas === 1 ? 'lugar vazio' : 'lugares vazios'}`)}>
          {Array.from({ length: 6 }, (_, i) => {
            if (s.jogadores && i >= rostos.length) {
              const k = i - rostos.length;
              if (k < nasVagas) return <span key={i} className="vaga" title="Bot no lugar vago"><IconeBot /></span>;
              if (k < bots.length) return <span key={i} className="ocupado bot"><IconeBot /></span>;
              return <span key={i} className="livre" />;
            }
            const j = rostos[i], av = s.jogadores ? (j ? imagemDoAvatar(j.avatar) : null) : i < s.avatares.length ? imagemDoAvatar(s.avatares[i] ?? null) : null;
            const classe = i >= s.sentados ? 'livre' : j && idsAmigos.has(j.id) ? 'ocupado amigo' : 'ocupado';
            return <span key={i} className={classe}>{av && <img src={av} alt="" />}</span>;
          })}
        </button>
        {assistir || botao ? <span className="botoes-sala">{assistir}{botao}</span> : null}
      </div>
      {quem && s.jogadores && <FolhaDePessoas titulo="Quem está na sala" subtitulo={s.nome} pessoas={s.jogadores} eu={sessao.id} aoFechar={() => setQuem(false)} />}
    </article>
  );
}

// Os olhos do Assistir.
function IconeOlhos() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>;
}

// O bot no rosto da lista (vaga aberta e mesa com bot).
function IconeBot() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="8" width="14" height="11" rx="3" /><path d="M12 4v4M9 13h.01M15 13h.01" /></svg>
  );
}

// Denunciar o nome: confirma antes, uma vez por conta. Convidado vê o convite para entrar com Google.
function Denunciar({ s, sessao }: { s: SalaNaLista; sessao: Sessao }) {
  const [passo, setPasso] = useState<'fechado' | 'confirmar' | 'enviando' | 'enviada'>(() => (jaDenunciou(s.id) ? 'enviada' : 'fechado'));
  const [aviso, setAviso] = useState<string | null>(null);
  if (passo === 'enviada') return <span className="denuncia-feita" role="status">Denúncia enviada</span>;
  const abrir = () => { setAviso(sessao.anonimo ? 'Entre com Google para denunciar.' : null); if (!sessao.anonimo) setPasso('confirmar'); };
  async function enviar() {
    setPasso('enviando');
    const r = await denunciarSala(sessao, s);
    if (r === 'ok' || r === 'ja') { setPasso('enviada'); setAviso(null); } else { setPasso('confirmar'); setAviso(r); }
  }
  return (
    <>
      <button className="denunciar" aria-expanded={passo !== 'fechado'} onClick={passo === 'fechado' ? abrir : () => setPasso('fechado')}>
        Denunciar nome
      </button>
      {aviso && passo === 'fechado' && <p className="nota denuncia-aviso" role="alert">{aviso}</p>}
      {passo !== 'fechado' && (
        <div className="confirma-denuncia" role="group" aria-label={`Denunciar o nome ${s.nome}`}>
          <p>Denunciar o nome "{s.nome}"? Com {DENUNCIAS_PARA_OCULTAR} denúncias, a sala sai da lista até a nossa revisão. Quem está nela segue jogando.</p>
          {aviso && <p className="erro" role="alert">{aviso}</p>}
          <div className="botoes-denuncia">
            <button className="btn2" onClick={() => { setPasso('fechado'); setAviso(null); }} disabled={passo === 'enviando'}>Cancelar</button>
            <button className="btn2 confirmar-denuncia" onClick={() => void enviar()} disabled={passo === 'enviando'}>
              {passo === 'enviando' ? 'Enviando...' : 'Denunciar'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
